'use client';

import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';

import IngredientDropdownButton from '@/app/(replicated)/ingredient-glossary/components/Dropdown';
import CheckIcon from '@/components/svg/reports-check-icon';

import 'react-circular-progressbar/dist/styles.css';

import ProgressBar from '@ramonak/react-progress-bar';
import { useState } from 'react';

import Image from 'next/image';

import DVIconBlack from '@/assets/icons/dv_icon_black.svg';
import DVIcon from '@/assets/icons/dv_icon.svg';
import {
  IbmPlexItalic,
  IbmPlexItalicBold,
  IbmPlexItalicMedium,
  IbmPlexItalicSemiBold,
  SweetSansPro,
  SweetSansProBold,
  SweetSansProExtraLight,
  SweetSansProLight,
  SweetSansProMedium,
  SweetSansProSemiBold,
} from '@/config/fonts';
import cn from '@/helpers/utils/classNames';
import { RankRequirement, RankUpWidgetResponse } from '@/types/Reports';

import ReportsLoader from '../CustomerDetailModal/ReportsLoader';

type RankUpProps = {
  data: RankUpWidgetResponse;
  setSelectedRankID: (id: number) => void;
  loading?: boolean;
  isTreeViewer?: boolean;
  isDashboard?: boolean;
  isFromViewProfile?: boolean;
};

const rankTextColor = [
  {
    rankID: 0,
    color: '#78A2E2',
    rankDescription: 'new petPro',
  },
  {
    rankID: 10,
    color: '#9D9090',
    rankDescription: 'petPro',
  },
  {
    rankID: 20,
    color: '#3B2122',
    rankDescription: 'Senior petPro',
  },
  {
    rankID: 30,
    color: '#8390A4',
    rankDescription: 'Director',
  },
  {
    rankID: 40,
    color: '#082149',
    rankDescription: 'Senior Director',
  },
  {
    rankID: 50,
    color: '#EFC6B6',
    rankDescription: 'Executive Director',
  },
  {
    rankID: 60,
    color: '#E08D6E',
    rankDescription: 'Vice President',
  },
  {
    rankID: 70,
    color: '#91A29A',
    rankDescription: 'One Star VP',
  },
  {
    rankID: 80,
    color: '#5A7468',
    rankDescription: 'Two Star VP',
  },
  {
    rankID: 90,
    color: '#234635',
    rankDescription: 'Three Star VP',
  },
];

const RankUpWidget = ({ data, setSelectedRankID, isTreeViewer, isDashboard, isFromViewProfile }: RankUpProps) => {
  const [selectedRankID, setRankID] = useState(data.nextRank?.rankID ?? 10);

  // Extract colors from API response
  const careerRankColor = { color: data?.careerRankColorCode };
  const paidRankColor = { color: data?.paidAsRankColorCode };
  const nextRankColor = { color: rankTextColor.find(rank => rank.rankID === selectedRankID)?.color || '' };
  const nextRankBarBackground = { backgroundColor: data?.nextRankColorCode };

  const percentage = +(data?.response?.totalPercentComplete ?? 0);

  const currentRankTitle =
    data?.ranks?.find(rank => rank.rankID === selectedRankID)?.rankDescription || 'No Rank Selected';

  const formatCurrency = (value: number) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  return (
    <div
      className={cn(
        SweetSansProExtraLight.variable,
        SweetSansProLight.variable,
        SweetSansPro.variable,
        SweetSansProMedium.variable,
        SweetSansProSemiBold.variable,
        SweetSansProBold.variable,
        IbmPlexItalic.variable,
        IbmPlexItalicBold.variable,
        IbmPlexItalicMedium.variable,
        IbmPlexItalicSemiBold.variable
      )}
    >
      {/* <div className="xxs:v2-max-h-[300px] md:v2-max-h-[350px] phonexii:v2-max-h-[dvh] v2-max-h-[500px] v2-overflow-auto"> */}
      <div
        className={cn(
          { 'v2-overflow-auto': !isFromViewProfile },
          {
            'xxs:v2-max-h-[50dvh] phonexii:v2-max-h-[58dvh] v2-max-h-[65dvh]': !isDashboard,
          }
        )}
      >
        <div className="v2-grid v2-gap-[20px]">
          {!isTreeViewer && !isDashboard && (
            <h3 className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-xl v2-text-[#101828]">Rank Up</h3>
          )}
          {!isFromViewProfile && (
            <hr
              className={cn('v2-mb-[12px]', {
                '': !isDashboard,
              })}
            />
          )}
          <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-[#344054]">
            Your Career Title is{' '}
            <span style={{ color: rankTextColor.find(rank => rank.rankID === data.careerRank.rankID)?.color || '' }}>
              {data?.careerRank?.rankDescription || 'petPro'}
            </span>{' '}
            and current qualified paid as title* is{' '}
            <span style={{ color: rankTextColor.find(rank => rank.rankID === data.paidAsRank.rankID)?.color || '' }}>
              {data?.paidAsRank?.rankDescription || 'petPro'}
            </span>
          </p>

          {/* Rank Progress Circle */}
          <div
            className={cn('v2-p-[12px] v2-bg-[#F9F9F9] v2-rounded-[10px] v2-flex v2-items-center v2-justify-between', {
              '!v2-bg-[#F9FAFB]': isFromViewProfile,
            })}
          >
            <div>
              <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-[#101828]">
                You are <span style={nextRankColor}>{percentage}%</span> to
              </p>
              <IngredientDropdownButton
                options={data.ranks}
                displayKey="rankDescription"
                valueKey="rankID"
                btnClassName="!v2-border-0 md:!v2-text-3xl !v2-text-[20px] v2-rounded-xs !v2-shadow-none !v2-bg-transparent v2-w-full !v2-text-[#101828] !v2-p-0"
                text={currentRankTitle}
                placeholder="Filter By Category"
                className="v2-w-[160px]"
                isFromPetRank={true}
                onSelect={val => {
                  setRankID(val.rankID);
                  setSelectedRankID(val.rankID);
                }}
              />
            </div>
            <div className="v2-w-[101px] v2-h-[101px]">
              <CircularProgressbar
                value={percentage}
                text={`${percentage}%`}
                strokeWidth={10}
                styles={buildStyles({
                  textSize: '20px',
                  pathColor: nextRankColor.color,
                  textColor: '#344054',
                  trailColor: '#E5E5E5',
                })}
              />
            </div>
          </div>

          {/* Volume Section */}
          <h4 className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-base v2-text-[#101828]">Volume</h4>
          {(['pvRequirement', 'dvRequirement', 'npV3Requirement'] as Array<keyof RankUpWidgetResponse>).map(
            requirementType => {
              const requirement = data?.[requirementType] as RankRequirement | null;
              if (!requirement) return null;

              // Ensure proper formatting with IsQualified check
              const displayedValue = requirement.isQualified
                ? requirement.requiredValueAsDecimal
                : requirement.actualValueAsDecimal;

              return (
                <div className="v2-relative" key={requirementType}>
                  <ProgressBar
                    completed={requirement.requiredToActualAsPercent ?? 0}
                    customLabel={`You have $${displayedValue.toLocaleString()} of $${requirement.requiredValueAsDecimal.toLocaleString()} in ${requirementType
                      .replace('Requirement', '')
                      .toUpperCase()}`}
                    height="28px"
                    borderRadius={isDashboard ? '4px' : undefined}
                    bgColor={nextRankColor.color}
                    baseBgColor="#E5E5E5"
                    labelAlignment="center"
                    labelClassName="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-transparent"
                  />
                  {isDashboard ? (
                    <span
                      className={cn(
                        'v2-absolute v2-top-[2px] v2-left-0 v2-w-full v2-font-sweet-sans-pro-medium v2-text-base v2-text-white v2-text-center v2-flex v2-items-center v2-justify-center v2-gap-1',
                        !requirement.requiredToActualAsPercent && '!v2-text-[#101828]'
                      )}
                    >
                      {`$${displayedValue.toLocaleString()} of $${requirement.requiredValueAsDecimal.toLocaleString()} in ${requirementType
                        .replace('Requirement', '')
                        .toUpperCase()}`}
                      {requirementType === 'dvRequirement' && selectedRankID > 40 && (
                        <Image src={DVIcon} alt="Right Icon" className="v2-z-10" />
                      )}
                    </span>
                  ) : (
                    <span
                      className={cn(
                        'v2-absolute v2-top-[2px] v2-left-0 v2-w-full v2-font-sweet-sans-pro-medium v2-text-base v2-text-white v2-text-center v2-flex v2-items-center v2-justify-center v2-gap-1',
                        !requirement.requiredToActualAsPercent && '!v2-text-[#101828]'
                      )}
                    >
                      {`You have $${displayedValue.toLocaleString()} of $${requirement.requiredValueAsDecimal.toLocaleString()} in ${requirementType
                        .replace('Requirement', '')
                        .toUpperCase()}`}
                      {requirementType === 'dvRequirement' && selectedRankID > 40 && (
                        <Image src={DVIcon} alt="Right Icon" className="v2-z-10" />
                      )}
                    </span>
                  )}
                </div>
              );
            }
          )}

          {/* Legs Section */}
          <h4 className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-base v2-text-[#101828]">Legs</h4>
          {(['activeLegs', 'builderLegs', 'vpLegs', 'oneStarVPLegs'] as Array<keyof RankUpWidgetResponse>).map(
            legType => {
              const legData = data?.[legType] as RankRequirement | null;
              if (!legData) return null;

              return (
                <div key={legType} className="v2-flex v2-items-center v2-gap-[12px]">
                  <span className="v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-[#101828] v2-capitalize">
                    {legType.includes('Star') ? 'Star' : legType.replace('Legs', '')}
                  </span>
                  <div className="v2-flex v2-items-center v2-gap-[8px]">
                    {Array.from({
                      length: Math.max(
                        0,
                        Number.isFinite(legData.requiredValueAsDecimal) ? Math.floor(legData.requiredValueAsDecimal) : 0
                      ),
                    }).map((_, i) => (
                      <CheckIcon key={i} color={i < legData.actualValueAsDecimal ? nextRankColor.color : '#D3D3D3'} />
                    ))}
                  </div>
                </div>
              );
            }
          )}

          <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-xs v2-text-[#101828]">
            *Subject to change based on cancellation at the end of the month
          </p>

          {!!data.mvrAmount && (
            <div className="v2-flex v2-items-center">
              {isDashboard ? (
                '*'
              ) : (
                <>
                  <Image src={DVIconBlack} alt="Right Icon" /> &nbsp;
                </>
              )}
              <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-xs v2-text-[#101828]">
                Maximum Volume Rule: Up to ${data.mvrAmount} of the required DV can come from any one leg
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RankUp = (props: Partial<RankUpProps>) => {
  if (props.loading) {
    return (
      <div className="v2-flex v2-justify-center v2-items-center md:v2-h-[60vh] v2-h-[40vh] v2-p-8">
        <ReportsLoader />
      </div>
    );
  }

  if (!props.loading && props.data && props.setSelectedRankID) {
    return (
      <RankUpWidget
        isTreeViewer={props.isTreeViewer}
        isDashboard={props.isDashboard}
        isFromViewProfile={props.isFromViewProfile}
        data={props.data}
        setSelectedRankID={props.setSelectedRankID}
      />
    );
  }
};
export default RankUp;
